'use client';

import { Calendar, CalendarProps } from '@/components/ui/calendar';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { add, format, isValid, Locale, parse } from 'date-fns';
import { enUS, vi } from 'date-fns/locale';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { useLocale } from 'next-intl';
import * as React from 'react';
import { useImperativeHandle, useRef } from 'react';
import { Matcher } from 'react-day-picker';
import { Input } from '../ui/input';
import TimePicker from './time-picker';
import { TimePickerInput } from './time-picker-input';
import { normalizeDateInput } from './utils';

type Granularity = 'day' | 'hour' | 'minute' | 'second';

type DateTimePickerProps = {
    id?: string;
    value?: Date;
    onChange?: (date: Date | undefined | null) => void;
    disabled?: boolean;
    /** showing `AM/PM` or not. */
    hourCycle?: 12 | 24;
    placeholder?: string;
    /**
     * The year range will be: `This year + yearRange` and `this year - yearRange`.
     * Default is 50.
     * For example:
     * This year is 2024, The year dropdown will be 1974 to 2024 which is generated by `2024 - 50 = 1974` and `2024 + 50 = 2074`.
     * */
    yearRange?: number;
    /**
     * The format is derived from the `date-fns` documentation.
     * @reference https://date-fns.org/v3.6.0/docs/format
     **/
    displayFormat?: { hour24?: string; hour12?: string };
    /**
     * The granularity prop allows you to control the smallest unit that is displayed by DateTimePicker.
     * By default, the value is `second` which shows all time inputs.
     **/
    granularity?: Granularity;

    /**Disable selected datetime */
    disabledDate?: Matcher | Matcher[];

    className?: string;
    inputClassName?: string;
    locale?: Locale;
} & Pick<CalendarProps, 'weekStartsOn' | 'showWeekNumber' | 'showOutsideDays'>;

type DateTimePickerRef = {
    value?: Date;
} & Omit<HTMLButtonElement, 'value'>;

const DateTimePicker = React.forwardRef<DateTimePickerRef, DateTimePickerProps>(
    (
        {
            id,
            locale = vi,
            value,
            onChange,
            hourCycle = 24,
            yearRange = 12,
            disabled = false,
            displayFormat,
            granularity = 'second',
            placeholder = 'Pick a date',
            className,
            inputClassName,
            disabledDate,
            ...props
        },
        ref
    ) => {
        const requestLocale = useLocale();

        const [isOpenPopover, setIsOpenPopover] = React.useState(false);
        const [month, setMonth] = React.useState<Date>(value ?? new Date());
        const [invalidDate, setInvalidDate] = React.useState(false);

        const dateInputRef = useRef<DateInputRefs>(null);
        const buttonRef = useRef<HTMLButtonElement>(null);
        /**
         * carry over the current time when a user clicks a new day
         * instead of resetting to 00:00
         */
        const handleSelect = (newDay: Date | undefined) => {
            if (!newDay) return;
            if (!value) {
                onChange?.(newDay);
                setMonth(newDay);
                if (dateInputRef.current) {
                    dateInputRef.current.onRevert(newDay);
                }
                return;
            }
            const diff = newDay.getTime() - value.getTime();
            const diffInDays = diff / (1000 * 60 * 60 * 24);
            const newDateFull = add(value, { days: Math.ceil(diffInDays) });

            onChange?.(newDateFull);
            setMonth(newDateFull);

            if (dateInputRef.current) {
                dateInputRef.current.onRevert(newDateFull);
            }
        };

        const onClear = () => {
            onChange?.(null);
            setMonth(new Date());
            dateInputRef.current?.clear();
        };

        useImperativeHandle(
            ref,
            () => ({
                ...buttonRef.current!,
                value,
            }),
            [value]
        );

        const initHourFormat = {
            day: 'dd/MM/yyyy',
            hour24: displayFormat?.hour24 ?? 'dd/MM/yyyy HH:mm:ss',
            hour12: displayFormat?.hour12 ?? 'dd/MM/yyyy hh:mm:ss a',
        };

        let dataLocale = locale;

        switch (requestLocale) {
            case 'en':
                dataLocale = enUS;
                break;

            case 'vi':
            default:
                break;
        }

        return (
            <div id={id} className='space-y-2'>
                <div
                    className={cn(
                        'relative flex items-center gap-1',
                        className
                    )}
                >
                    <DateInput
                        ref={dateInputRef}
                        initHourFormat={initHourFormat}
                        granularity={granularity}
                        onOpenChange={setIsOpenPopover}
                        dateValue={value}
                        onSelectDateValue={handleSelect}
                        placeholder={placeholder}
                        invalidDate={invalidDate}
                        setInvalidDate={setInvalidDate}
                        readOnly={disabled}
                        className={cn('pr-7', inputClassName, {
                            'pr-16': !!value,
                        })}
                    />

                    {!!value && !disabled && (
                        <div className='absolute right-9 top-1/2 -translate-y-1/2'>
                            <button
                                onClick={onClear}
                                className={cn(
                                    'w-fit gap-0 rounded-md p-1.5 hover:bg-red-100 hover:text-red-500'
                                )}
                            >
                                <X className='h-4 w-4' />
                            </button>
                        </div>
                    )}

                    <Popover
                        open={isOpenPopover}
                        onOpenChange={setIsOpenPopover}
                    >
                        <div className='absolute right-1 top-1/2 -translate-y-1/2'>
                            <PopoverTrigger asChild disabled={disabled}>
                                <button
                                    className={cn(
                                        'w-fit gap-0 rounded-md p-1.5 hover:bg-primary/10'
                                    )}
                                    ref={buttonRef}
                                >
                                    <CalendarIcon className='h-4 w-4' />
                                </button>
                            </PopoverTrigger>
                        </div>

                        <PopoverContent className='w-auto p-0' align='end'>
                            <Calendar
                                mode='single'
                                selected={value}
                                month={month}
                                onSelect={(d) => handleSelect(d)}
                                onMonthChange={handleSelect}
                                yearRange={yearRange}
                                locale={dataLocale}
                                classNames={{
                                    root: '!w-72', //TODO: custom styles canlendar
                                    weeks: '[&>*]:gap-1', //TODO: custom styles canlendar
                                    weekdays: `flex w-full gap-1 [&>*]:w-[34px]`, //TODO: custom styles canlendar
                                }}
                                disabled={disabledDate}
                                {...props}
                            />
                            {granularity !== 'day' && (
                                <div className='border-t border-border p-3'>
                                    <TimePicker
                                        setDate={(data) => {
                                            onChange?.(data);
                                            if (dateInputRef.current && data) {
                                                dateInputRef.current.onRevert(
                                                    data
                                                );
                                            }
                                        }}
                                        date={value}
                                        hourCycle={hourCycle}
                                        granularity={granularity}
                                    />
                                </div>
                            )}
                        </PopoverContent>
                    </Popover>
                </div>

                {invalidDate && (
                    <span className='block text-xs font-medium text-destructive'>
                        Ngày không hợp lệ
                    </span>
                )}
            </div>
        );
    }
);

DateTimePicker.displayName = 'DateTimePicker';

type DateInputProps = {
    granularity: Granularity;
    initHourFormat: { day: string; hour24: string; hour12: string };
    onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;

    dateValue?: Date;
    onSelectDateValue?: (date?: Date) => void;

    invalidDate?: boolean;
    setInvalidDate: React.Dispatch<React.SetStateAction<boolean>>;

    placeholder?: string;
    readOnly?: boolean;
    className?: string;
    id?: string;
};

type DateInputRefs = {
    onRevert: (dta: Date) => void;
    clear: VoidFunction;
};

const DateInput = React.memo(
    React.forwardRef<DateInputRefs, DateInputProps>((props, ref) => {
        const {
            id,
            granularity,
            onOpenChange,
            initHourFormat,
            onSelectDateValue,
            dateValue,
            invalidDate = false,
            setInvalidDate,

            ...rest
        } = props;

        // Hold the input value in state
        const [inputStrValue, setInputStrValue] = React.useState('');

        /**
         * Handle the input change event: parse the input value to a date, update the
         * selected date and set the month.
         */
        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setInputStrValue(e.target.value); // keep the input value in sync

            let formatString = initHourFormat.day;
            if (!e.target.value) {
                onSelectDateValue?.();
                setInvalidDate(false);
                return;
            }

            const current = normalizeDateInput(e.target.value);
            const strLeng = current.length;

            if (strLeng > 0 && strLeng < 10) {
                setInvalidDate(true);
                return;
            }

            const parsedDate = parse(current, formatString, new Date());

            if (isValid(parsedDate)) {
                onSelectDateValue?.(parsedDate);
                setInvalidDate(false);
            } else {
                onSelectDateValue?.();
                setInvalidDate(true);
            }
        };

        const onRevertInputDateData = React.useCallback(
            (dta: Date) => {
                setInputStrValue(
                    granularity === 'day'
                        ? format(dta, initHourFormat.day)
                        : format(
                              dta,
                              initHourFormat[
                                  granularity === 'second' ? 'hour24' : 'hour12'
                              ]
                          )
                );
            },
            [granularity, initHourFormat, setInputStrValue]
        );

        const handleOnBlur = React.useCallback(() => {
            if (invalidDate) {
                if (dateValue instanceof Date) {
                    onRevertInputDateData(dateValue);
                } else {
                    setInvalidDate(false);
                    setInputStrValue('');
                }
            }
        }, [invalidDate, dateValue, onRevertInputDateData, setInvalidDate]);

        const _initedData = useRef<boolean>(false);
        React.useEffect(() => {
            if (_initedData.current) return;
            if (dateValue instanceof Date) {
                onRevertInputDateData(dateValue);
                _initedData.current = true;
            }
        }, [onRevertInputDateData, dateValue]);

        useImperativeHandle(ref, () => {
            return {
                onRevert: onRevertInputDateData,
                clear: () => {
                    setInputStrValue('');
                },
            };
        }, [onRevertInputDateData]);

        return (
            <Input
                id={id}
                type='text'
                value={inputStrValue}
                placeholder='dd/mm/yyyy'
                onChange={handleInputChange}
                onBlur={handleOnBlur}
                {...rest}
            />
        );
    })
);

DateInput.displayName = 'DateInput';

export { DateTimePicker, TimePickerInput };
export type { DateTimePickerProps, DateTimePickerRef };
